import { Add, DeleteOutline } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  alpha,
} from '@mui/material';
import type {
  ILegalDependencyConditionGroupProps,
  LogicalOperator,
} from '../../../Interfaces/ILegalDependency';
import { LegalDependencyConditionRow } from './LegalDependencyConditionRow';

export const LegalDependencyConditionGroup = ({
  getRequirementOption,
  getStatusLabel,
  getTransactionOption,
  group,
  groupIndex,
  groupLoading,
  isLoadingLegalTransactions,
  legalRequirementStates,
  onAddCondition,
  onChangeCondition,
  onChangeOperator,
  onRemoveCondition,
  onRemoveGroup,
  removeGroupDisabled,
  requirementOptionsByTransaction,
  transactionOptions,
  isTransactionLoading,
}: ILegalDependencyConditionGroupProps) => (
  <Stack spacing={1.25}>
    {groupIndex > 0 ? (
      <Autocomplete<LogicalOperator, false, true, false>
        disableClearable
        onChange={(_, value) => onChangeOperator(value)}
        options={['UND', 'ODER']}
        renderInput={params => (
          <TextField
            {...params}
            label='Operator zur vorherigen Gruppe'
            placeholder='UND oder ODER'
            size='small'
          />
        )}
        size='small'
        value={group.operator || 'UND'}
      />
    ) : null}

    <Box
      sx={{
        border: theme => `1px solid ${theme.palette.divider}`,
        borderRadius: 2.5,
        p: 1.75,
        bgcolor: theme => alpha(theme.palette.info.light, 0.04),
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          alignItems='center'
          direction='row'
          justifyContent='space-between'
        >
          <Stack alignItems='center' direction='row' spacing={1}>
            <Chip
              label={`Gruppe ${groupIndex + 1}`}
              size='small'
              variant='outlined'
            />
            <Chip
              label={`${group.conditions.length} Bedingung${
                group.conditions.length === 1 ? '' : 'en'
              } innerhalb der Gruppe mit UND`}
              size='small'
              variant='outlined'
            />
          </Stack>
          {groupLoading ? <CircularProgress size={18} /> : null}
        </Stack>

        <Stack spacing={1.5}>
          {group.conditions.map((condition, conditionIndex) => {
            const requirementOptions =
              condition.transactionId !== null
                ? requirementOptionsByTransaction.get(
                    condition.transactionId
                  ) || []
                : [];
            const transactionLoading = isTransactionLoading(
              condition.transactionId
            );

            return (
              <LegalDependencyConditionRow
                condition={condition}
                conditionIndex={conditionIndex}
                getRequirementOption={getRequirementOption}
                getStatusLabel={getStatusLabel}
                getTransactionOption={getTransactionOption}
                isLoadingLegalTransactions={isLoadingLegalTransactions}
                key={condition.id}
                legalRequirementStates={legalRequirementStates}
                onChange={updates => onChangeCondition(condition.id, updates)}
                onRemove={() => onRemoveCondition(condition.id)}
                removeDisabled={group.conditions.length === 1}
                requirementOptions={requirementOptions}
                transactionLoading={transactionLoading}
                transactionOptions={transactionOptions}
              />
            );
          })}
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent='space-between'
          spacing={1}
        >
          <Button
            onClick={onAddCondition}
            startIcon={<Add />}
            variant='outlined'
          >
            Weitere "UND" Bedingung
          </Button>
          <Button
            color='error'
            disabled={removeGroupDisabled}
            onClick={onRemoveGroup}
            startIcon={<DeleteOutline />}
            variant='text'
          >
            Gruppe löschen
          </Button>
        </Stack>
      </Stack>
    </Box>
  </Stack>
);
